import React, { useEffect, useState, useContext } from "react";
import { PatientContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";

// hooks
import { useHistory, useLocation } from "react-router-dom";
import useListSelector from "../../lib/useListSelector";

// components
import Sallie from "../UIComponents/Sallie";
import StrokeListSelecter from "../UIComponents/StrokeListSelecter";
import SubmitButton from "../UIComponents/SubmitButton";
import OnboardingStepHeader from "./OnboardingStepHeader";

// lib
import { Form } from "react-bootstrap";

// api
import { updateSmiDetail } from "../../api/smiRequests";

// NOTE: this file resolves its own patient/caregiver text directly off
// user.userType, the same way RecentSMI1.js does, rather than going
// through customizeMessages (which keys off user.patientType and can
// come back blank when this route is loaded outside the full
// registration flow).

// Saves to PUT /record-builder/smi-detail (no registration step; the
// resume check sends step3-incomplete patients back here).
const RecentSMI2 = ({ setOnboardingPercent, user }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [returnedData, setReturnedData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(false);
  const history = useHistory();
  const location = useLocation();

  const categories = [
    // MERGED into earlyWarningSigns (SMI onboarding redesign)
    // {
    //   category: "symptomsNoticed",
    //   items: Array.from({ length: 10 }, (_, i) =>
    //     t(`recentsmi2.symptomsNoticed.options.${i}`)
    //   ),
    // },
    {
      category: "earlyWarningSigns",
      // Dropped from the original list (strings kept in en/es.json under
      // recentsmi2.earlyWarningSigns.removed.*):
      //   - Stopping or skipping medication
      //   - Increased alcohol or substance use
      //   - Hallucinations or paranoia   (covered by options 0 & 1)
      //   - Stopping self-care           (covered by option 7)
      items: Array.from({ length: 10 }, (_, i) =>
        t(`recentsmi2.earlyWarningSigns.options.${i}`)
      ),
    },
    {
      category: "firstToNotice",
      items: Array.from({ length: 4 }, (_, i) =>
        t(`recentsmi2.firstToNotice.options.${i}`)
      ),
    },
    {
      category: "crisisHistory",
      items: Array.from({ length: 6 }, (_, i) =>
        t(`recentsmi2.crisisHistory.options.${i}`)
      ),
    },
    {
      // NOTE: wording flagged by product as placeholder — pending a
      // follow-up review noted in the source doc ("see flag below")
      // that wasn't available when this was built. Confirm final
      // copy before this ships.
      category: "currentSafetyConcern",
      items: Array.from({ length: 3 }, (_, i) =>
        t(`recentsmi2.currentSafetyConcern.options.${i}`)
      ),
    },
  ];

  const smiInfo = categories.map(({ category, items }) => ({
    category,
    items,
  }));

  const { toggleFromSelectedList, switchSingleSelection, addOther, selectedData, initialize } =
    useListSelector({
      setInitialData: setReturnedData,
      initialData: returnedData,
    });

  const onSubmit = async () => {
    const assignedData = {
      // symptomsNoticed: selectedData.symptomsNoticed,
      symptomsNoticed: [], // merged into earlyWarningSigns; sent empty for BE compatibility
      earlyWarningSigns: selectedData.earlyWarningSigns,
      firstToNotice: selectedData.firstToNotice,
      crisisHistory: selectedData.crisisHistory,
      currentSafetyConcern: selectedData.currentSafetyConcern[0],
    };
    if (
      // assignedData.symptomsNoticed.length === 0 ||
      assignedData.earlyWarningSigns.length === 0 ||
      assignedData.firstToNotice.length === 0 ||
      assignedData.crisisHistory.length === 0 ||
      !assignedData.currentSafetyConcern
    ) {
      setErrorMessage(true);
      return;
    }
    setSubmitting(true);

    try {
      await updateSmiDetail({
        patientId: patient.patientId,
        data: assignedData,
      });
    } catch (err) {
      console.error("updateSmiDetail", err);
      setSubmitting(false);
      history.push("/oops");
      return;
    }

    setSubmitting(false);
    history.push("/RecentSMI3", {
      fromPatientControl: location.state?.fromPatientControl,
      date: location.state?.date,
    });
  };

  useEffect(() => {
    location.state?.fromPatientControl
      ? setOnboardingPercent(40)
      : setOnboardingPercent(30);
    setReturnedData(smiInfo);
    initialize(smiInfo);
    setLoading(false);
  }, [t]);

  const isCaregiver = user?.userType === "caregiver";

  const getMessage = (key) => {
    const messages = {
      mainHeader: {
        patient: t("recentsmi2.header"),
        caregiver: t("recentsmi2.header"),
      },
      sallieText: {
        patient: t("recentsmi2.sallie.patient"),
        caregiver: t("recentsmi2.sallie.caregiver"),
      },
      // symptomsNoticed: {
      //   patient: t("recentsmi2.symptomsNoticed.patient"),
      //   caregiver: t("recentsmi2.symptomsNoticed.caregiver"),
      // },
      earlyWarningSigns: {
        patient: t("recentsmi2.earlyWarningSigns.patient"),
        caregiver: t("recentsmi2.earlyWarningSigns.caregiver"),
      },
      firstToNotice: {
        patient: t("recentsmi2.firstToNotice.patient"),
        caregiver: t("recentsmi2.firstToNotice.caregiver"),
      },
      crisisHistory: {
        patient: t("recentsmi2.crisisHistory.patient"),
        caregiver: t("recentsmi2.crisisHistory.caregiver"),
      },
      currentSafetyConcern: {
        patient: t("recentsmi2.currentSafetyConcern.patient"),
        caregiver: t("recentsmi2.currentSafetyConcern.caregiver"),
      },
    };
    return isCaregiver ? messages[key].caregiver : messages[key].patient;
  };

  const options = {
    // symptomsNoticed: { message: "symptomsNoticed" },
    earlyWarningSigns: { message: "earlyWarningSigns" },
    firstToNotice: { message: "firstToNotice" },
    crisisHistory: { message: "crisisHistory" },
    currentSafetyConcern: {
      message: "currentSafetyConcern",
      responseNumber: 1,
    },
  };

  return (
    <>
      {loading ? (
        t("common.loading")
      ) : (
        <div className="d-block">
          <OnboardingStepHeader text={getMessage("mainHeader")} />
          <Sallie text={getMessage("sallieText")} />

          {returnedData.map((itemList, i) => {
            const { category } = itemList;
            return (
              <StrokeListSelecter
                key={category}
                category={category}
                message={getMessage(options[category].message)}
                list={itemList.items}
                index={i}
                maxSelectAmount={options[category].responseNumber}
                selectedList={selectedData[category]}
                toggleFromSelectedList={toggleFromSelectedList}
                selectOne={switchSingleSelection}
                addOther={addOther}
              />
            );
          })}
          {errorMessage && (
            <Form.Control.Feedback
              style={{ display: "block" }}
              type="invalid"
            >
              {t("recentsmi2.choose.error")}
            </Form.Control.Feedback>
          )}
          <SubmitButton disabled={submitting} onClick={onSubmit} type="submit">
            {t("onboarding.saveContinue")} &gt;
          </SubmitButton>
        </div>
      )}
    </>
  );
};

export default RecentSMI2;